// Billing card for an invoice-billed organization (Stripe send_invoice, paid by bank transfer (EFT, ACH or SEPA)).
// No card actions: no Manage Billing (Stripe portal would offer to add a card), no
// Reactivate (card checkout would open a second subscription), no interval switch.
// Plan changes are made by our team (contact box below); portal-api also answers upgrade-options with flow_type 'request' as a backstop.
import { useSearchParams } from 'react-router-dom';
import Card from '../common/Card';
import { useBillingInvoices } from '../../hooks/useBilling';
import type { BillingInvoice, BillingStatus, Subscription } from '../../types';

interface Props {
  billing: BillingStatus;
  subscriptions?: Subscription[];
}

// Exported for unit testing the state rule.
// eslint-disable-next-line react-refresh/only-export-components
export function invoiceState(inv: BillingInvoice, now = Date.now()): 'paid' | 'overdue' | 'open' {
  if (inv.status === 'paid') return 'paid';
  return inv.due_date && Date.parse(inv.due_date) < now ? 'overdue' : 'open';
}

const money = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '');

const STATE_CLASS = { paid: 'bg-green-100 text-green-800', overdue: 'bg-red-100 text-red-800', open: 'bg-amber-100 text-amber-800' } as const;
const STATE_LABEL = { paid: 'Paid', overdue: 'Overdue', open: 'Open' } as const;

export default function InvoiceBillingCard({ billing, subscriptions }: Props) {
  const [searchParams] = useSearchParams();
  const upgradeLinked = searchParams.has('upgrade');
  const { data: invoices, isLoading, isError } = useBillingInvoices();

  const suspended = subscriptions?.filter((s) => s.status === 'suspended') ?? [];
  // Phase 1 has one subscription per invoice-billed org, so the newest overdue invoice is the right one for every banner.
  const overdue = (invoices ?? []).find((i) => invoiceState(i) === 'overdue');

  return (
    <Card className="p-6">
      {upgradeLinked && (
        <p className="text-sm mb-4 text-amber-700">
          Plan changes for invoice-billed services are made by our team. Contact us below and we'll make the change.
        </p>
      )}
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Billing Information</h2>
      <p className="text-gray-600 mb-4">
        Your service is billed by invoice. Invoices are emailed to your billing contact and paid by bank transfer using the details on each
        invoice.
      </p>

      {suspended.map((s) => (
        <div key={s.id} className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-sm text-red-800">
          Service for {s.uos_hostname ?? s.hostname} is suspended because an invoice is overdue.{' '}
          {overdue?.hosted_invoice_url ? (
            <a className="underline font-medium" href={overdue.hosted_invoice_url} target="_blank" rel="noreferrer">
              Pay invoice {overdue.number ?? ''}
            </a>
          ) : (
            isError ? 'Pay the overdue invoice we emailed to your billing contact.' : 'Pay the open invoice below.'
          )}{' '}
          Service is restored as soon as we record your payment.
        </div>
      ))}

      <div className="border border-gray-200 rounded-md p-4 mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Services</h3>
        <ul className="space-y-1 text-sm">
          {(billing.subscriptions ?? []).map((s) => (
            <li key={s.subscription_id} className="flex justify-between">
              <span className="text-gray-900">{s.hostname ?? s.subscription_id}</span>
              <span className="flex items-center gap-2">
                <span className="text-gray-600">{s.plan_name}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{s.status}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="border border-gray-200 rounded-md p-4 mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Invoices</h3>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading invoices…</p>
        ) : isError ? (
          <p className="text-sm text-red-600">Could not load invoices. Please try again later.</p>
        ) : (invoices ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">No invoices yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-1">Invoice</th>
                <th>Date</th>
                <th>Due</th>
                <th>Amount</th>
                <th>Status</th>
                <th>
                  <span className="sr-only">Links</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices!.map((i) => {
                const state = invoiceState(i);
                return (
                  <tr key={i.id} className="border-t border-gray-100">
                    <td className="py-1">{i.number ?? i.id}</td>
                    <td>{day(i.created)}</td>
                    <td>{day(i.due_date)}</td>
                    <td>{money(i.total_cents, i.currency)}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATE_CLASS[state]}`}>{STATE_LABEL[state]}</span>
                    </td>
                    <td className="text-right space-x-3">
                      {i.hosted_invoice_url && (
                        <a className="text-blue-700 underline" href={i.hosted_invoice_url} aria-label={`View invoice ${i.number ?? i.id}`} target="_blank" rel="noreferrer">
                          View invoice
                        </a>
                      )}
                      {i.invoice_pdf && (
                        <a className="text-blue-700 underline" href={i.invoice_pdf} aria-label={`PDF of invoice ${i.number ?? i.id}`} target="_blank" rel="noreferrer">
                          PDF
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <p className="text-sm text-gray-600 mb-3">
          To change your plan, or for questions about an invoice, a purchase order or your billing contact, contact us.
        </p>
        <a
          href="mailto:support@cloudunifi.com"
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Contact us
        </a>
      </div>

    </Card>
  );
}
